import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, FileText, LogOut, Receipt, ShieldCheck } from 'lucide-react';
import type { PortalDocumentKind, PortalPatientOption, PortalTimelineEntry } from '@his/shared';
import { cn, dateTime, humanize, money } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { printDocument } from '@/lib/print';
import { Alert, Badge, BrandLoader, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, FormSkeleton, Input } from '@/components/ui/primitives';
import { Dialog } from '@/components/ui/dialog';
import { useTranslation } from '@/lib/i18n';
import { FloatingThemeToggle, ThemeToggle } from '@/components/ThemeToggle';
import { PortalBookingPage } from './PortalBookingPage';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const TOKEN_KEY = 'his.portal.token';
const TENANT_KEY = 'his.portal.tenant';

/**
 * A tiny fetch client of its own.
 *
 * The staff client refreshes tokens, handles break-glass headers and clears the
 * staff session on 401 — none of which a patient's browser should inherit. A
 * portal token is short-lived and re-obtained by OTP, so the simplest possible
 * client is also the safest one.
 */
export async function portalFetch<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const tenant = localStorage.getItem(TENANT_KEY) ?? window.location.hostname.split('.')[0];

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenant,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const message = (payload as { error?: { message?: string } } | null)?.error?.message ?? 'Something went wrong';
    throw new Error(message);
  }
  return payload as T;
}

export interface PortalPatient {
  id: string;
  mrn: string;
  fullName: string;
  phone: string | null;
  bloodGroup: string | null;
  allergies: { substance: string; reaction: string | null }[];
  changesByPhoneOnly?: boolean;
}

/**
 * Patient portal (SRS 11.1).
 *
 * Deliberately separate from the staff application: its own shell, its own
 * token store and its own fetch client, so nothing a patient's session holds
 * can be mistaken for a staff session.
 */
export function PortalApp() {
  const [params] = useSearchParams();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    const tenant = params.get('tenant');
    if (tenant) localStorage.setItem(TENANT_KEY, tenant);
  }, [params]);

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token)
    return (
      <>
        <PortalSignIn onSignedIn={setToken} />
        <FloatingThemeToggle />
      </>
    );

  return (
    <div className="mx-auto min-h-screen w-full max-w-3xl px-4 py-6">
      <header className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">My health record</h1>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <Routes>
        <Route path="book" element={<PortalBookingPage />} />
        <Route path="*" element={<PortalHome />} />
      </Routes>
    </div>
  );
}

function PortalHome() {
  const [tab, setTab] = useState<'timeline' | 'appointments' | 'bills'>('appointments');

  return (
    <>
      <PortalIdentity />
      <PortalReportLink />

      <nav className="mb-4 flex gap-1 border-b" role="tablist">
        {(
          [
            ['appointments', 'Appointments', CalendarDays],
            ['timeline', 'My record', FileText],
            ['bills', 'Bills', Receipt],
          ] as const
        ).map(([id, label, Icon]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium focus-ring',
              tab === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground',
            )}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      {tab === 'appointments' && <PortalAppointments />}
      {tab === 'timeline' && <PortalTimeline />}
      {tab === 'bills' && <PortalBills />}
    </>
  );
}

type VerifyAnswer = { accessToken: string } | { choosePatient: PortalPatientOption[] };

function PortalSignIn({ onSignedIn }: { onSignedIn: (token: string) => void }) {
  const { t } = useTranslation('portal');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<PortalPatientOption[] | null>(null);
  const [patientId, setPatientId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  const request = useMutation({
    mutationFn: () => portalFetch('/portal/auth/otp', { method: 'POST', body: { phone } }),
    onSuccess: () => {
      setSent(true);
      setError(null);
      setOptions(null);
      setPatientId('');
      setDateOfBirth('');
    },
    onError: (caught: Error) => setError(caught.message),
  });

  const verify = useMutation({
    mutationFn: () =>
      portalFetch<VerifyAnswer>('/portal/auth/verify', {
        method: 'POST',
        body: { phone, code, ...(patientId ? { patientId, dateOfBirth: dateOfBirth || undefined } : {}) },
      }),
    onSuccess: (result) => {
      if ('choosePatient' in result) {
        setOptions(result.choosePatient);
        setError(null);
        return;
      }
      localStorage.setItem(TOKEN_KEY, result.accessToken);
      onSignedIn(result.accessToken);
    },
    onError: (caught: Error) => setError(caught.message),
  });
  const choosing = Boolean(options?.length);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('signin.heading')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('signin.hint')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label={t('signin.phone')} required>
            <Input
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="03001234567"
              inputMode="tel"
              disabled={sent}
            />
          </Field>

          {sent && (
            <Field label={t('signin.code_six')} required hint={t('signin.code_hint')}>
              <Input
                dir="ltr"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoFocus
                disabled={choosing}
              />
            </Field>
          )}

          {choosing && (
            <div className="space-y-3 rounded-md border p-3">
              <p className="text-sm">{t('signin.choose_patient')}</p>
              <div className="space-y-1" role="radiogroup" aria-label="Patient">
                {options?.map((option) => (
                  <label key={option.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="portal-patient"
                      value={option.id}
                      checked={patientId === option.id}
                      onChange={() => setPatientId(option.id)}
                    />
                    {option.name}
                  </label>
                ))}
              </div>
              <Field label={t('signin.date_of_birth')} required>
                <DatePicker value={dateOfBirth} onChange={setDateOfBirth} />
              </Field>
            </div>
          )}

          {error && <Alert variant="destructive">{error}</Alert>}

          {sent ? (
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => verify.mutate()}
                loading={verify.isPending}
                disabled={code.length !== 6 || (choosing && (!patientId || !dateOfBirth))}
              >
                {t('signin.verify')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setSent(false);
                  setOptions(null);
                  setPatientId('');
                  setDateOfBirth('');
                }}
              >
                {t('signin.change_number')}
              </Button>
            </div>
          ) : (
            <Button className="w-full" onClick={() => request.mutate()} loading={request.isPending} disabled={phone.length < 7}>
              {t('signin.send_code')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PortalIdentity() {
  const me = useQuery({ queryKey: ['portal-me'], queryFn: () => portalFetch<PortalPatient>('/portal/me') });

  if (me.isLoading) {
    return (
      <BrandLoader className="py-16" />
    );
  }
  if (!me.data) return null;

  return (
    <Card className="mb-4">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <p className="text-lg font-semibold">{me.data.fullName}</p>
          <p className="text-sm text-muted-foreground">
            {me.data.mrn}
            {me.data.bloodGroup && me.data.bloodGroup !== 'unknown'
              ? ` · ${me.data.bloodGroup.replace('_pos', '+').replace('_neg', '−').toUpperCase()}`
              : ''}
          </p>
        </div>
        {me.data.allergies.length > 0 && (
          <Alert variant="destructive" className="w-full sm:w-auto">
            <span className="font-semibold">Allergies</span>{' '}
            {me.data.allergies.map((a) => a.substance).join(', ')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function PortalAppointments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['portal-me'], queryFn: () => portalFetch<PortalPatient>('/portal/me') });
  const byPhoneOnly = Boolean(me.data?.changesByPhoneOnly);

  const appointments = useQuery({
    queryKey: ['portal-appointments'],
    queryFn: () =>
      portalFetch<
        {
          id: string;
          slotAt: string;
          status: string;
          tokenNo: string | null;
          doctor: { name: string; designation: string | null };
          branch: { name: string; address: string | null };
          patientsAhead: number | null;
        }[]
      >('/portal/appointments'),
    refetchInterval: visibleInterval(60_000),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => portalFetch(`/portal/appointments/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-appointments'] }),
  });
  const confirm = useMutation({
    mutationFn: (id: string) => portalFetch(`/portal/appointments/${id}/confirm`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-appointments'] }),
  });

  if (appointments.isLoading) return <FormSkeleton />;

  const bookButton = !byPhoneOnly && (
    <div className="flex justify-end">
      <Button size="sm" onClick={() => navigate('/portal/book')}>
        Book an appointment
      </Button>
    </div>
  );

  if (!appointments.data?.length) {
    return (
      <div className="space-y-3">
        {byPhoneOnly && <Alert>To book, change or cancel an appointment, please call the hospital.</Alert>}
        {bookButton}
        <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">No upcoming appointments.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {byPhoneOnly && <Alert>To book, change or cancel an appointment, please call the hospital.</Alert>}
      {bookButton}
      {cancel.error && <Alert variant="destructive">{(cancel.error as Error).message}</Alert>}
      {confirm.error && <Alert variant="destructive">{(confirm.error as Error).message}</Alert>}
      {appointments.data.map((appointment) => (
        <Card key={appointment.id}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">{appointment.doctor.name}</p>
                <p className="text-sm text-muted-foreground">
                  {appointment.doctor.designation} · {appointment.branch.name}
                </p>
                <p className="mt-1 text-sm">{dateTime(appointment.slotAt)}</p>
              </div>
              <div className="text-end">
                <Badge variant={appointment.status === 'arrived' ? 'warning' : 'secondary'}>
                  {humanize(appointment.status)}
                </Badge>
                {appointment.tokenNo && <p className="mt-1 text-2xl font-semibold tabular">{appointment.tokenNo}</p>}
              </div>
            </div>

            {appointment.patientsAhead !== null && (
              <Alert className="mt-3">
                {appointment.patientsAhead === 0
                  ? 'You are next.'
                  : `${appointment.patientsAhead} patient${appointment.patientsAhead === 1 ? '' : 's'} ahead of you.`}
              </Alert>
            )}

            {!byPhoneOnly && ['booked', 'confirmed'].includes(appointment.status) && (
              <div className="mt-3 flex gap-2">
                {appointment.status === 'booked' && (
                  <Button size="sm" onClick={() => confirm.mutate(appointment.id)} loading={confirm.isPending}>
                    I will attend
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => cancel.mutate(appointment.id)} loading={cancel.isPending}>
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PortalTimeline() {
  const timeline = useQuery({
    queryKey: ['portal-timeline'],
    queryFn: () => portalFetch<PortalTimelineEntry[]>('/portal/timeline'),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, PortalTimelineEntry[]>();
    for (const entry of timeline.data ?? []) {
      const day = entry.at.slice(0, 10);
      map.set(day, [...(map.get(day) ?? []), entry]);
    }
    return [...map.entries()];
  }, [timeline.data]);

  if (timeline.isLoading) return <FormSkeleton />;
  if (grouped.length === 0) {
    return <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">Nothing on your record yet.</p>;
  }

  return (
    <div className="space-y-5">
      {grouped.map(([day, entries]) => (
        <div key={day}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day}</p>
          <div className="space-y-2">
            {entries.map((entry) => (
              <div key={`${entry.kind}-${entry.refId}`} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{entry.title}</p>
                  <Badge variant="muted">{humanize(entry.kind)}</Badge>
                </div>
                {entry.detail && <p className="text-sm text-muted-foreground">{humanize(entry.detail)}</p>}
                {entry.downloadable && PORTAL_DOCUMENT_KINDS_SHOWN.includes(entry.kind) && (
                  <PortalDocumentActions kind={entry.kind as PortalDocumentKind} id={entry.refId} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PortalBills() {
  const bills = useQuery({
    queryKey: ['portal-bills'],
    queryFn: () =>
      portalFetch<
        { id: string; number: string; context: string; createdAt: string; netMinor: number; dueMinor: number }[]
      >('/portal/bills'),
  });

  if (bills.isLoading) return <FormSkeleton />;
  if (!bills.data?.length) {
    return <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">Nothing outstanding.</p>;
  }

  return (
    <div className="space-y-3">
      <Alert>Pay at the reception. Online payment is not available.</Alert>
      {bills.data.map((bill) => (
        <Card key={bill.id}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">{bill.number}</p>
                <p className="text-sm text-muted-foreground">
                  {humanize(bill.context)} · {dateTime(bill.createdAt)}
                </p>
              </div>
              <div className="text-end">
                <p className="text-lg font-semibold tabular">{money(bill.dueMinor)}</p>
                <p className="text-xs text-muted-foreground">of {money(bill.netMinor)}</p>
              </div>
            </div>
            <PortalDocumentActions kind="invoice" id={bill.id} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export interface PortalDoctor {
  id: string;
  name: string;
  designation: string | null;
  department: string | null;
  specialty: string | null;
  branches: { id: string; name: string }[];
}

interface RenderedDocument {
  format: 'html' | 'pdf';
  html: string;
  pdfBase64: string | null;
  filename: string;
}

const PORTAL_DOCUMENT_KINDS_SHOWN: readonly string[] = ['prescription', 'invoice', 'certificate', 'lab', 'radiology'];

export function reportFromPath(pathname: string): { kind: PortalDocumentKind | null; id: string } | null {
  const match = /\/portal\/reports\/(?:(lab|radiology)\/)?([0-9a-f-]{36})\/?$/i.exec(pathname);
  if (!match) return null;
  return { kind: (match[1]?.toLowerCase() as PortalDocumentKind | undefined) ?? null, id: match[2] };
}

function PortalReportLink() {
  const location = useLocation();
  const target = reportFromPath(location.pathname);
  const targetKind = target?.kind ?? null;
  const targetId = target?.id ?? null;
  const [doc, setDoc] = useState<RenderedDocument | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    const kinds: PortalDocumentKind[] = targetKind ? [targetKind] : ['lab', 'radiology'];
    void (async () => {
      for (const kind of kinds) {
        try {
          const found = await portalFetch<RenderedDocument>(`/portal/documents/${kind}/${targetId}?format=html`);
          if (!cancelled) setDoc(found);
          return;
        } catch {
          continue;
        }
      }
      if (!cancelled) setFailed(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [targetKind, targetId]);
  if (!target) return null;
  if (failed) return <Alert variant="warning" className="mb-4">That report is not ready yet, or it belongs to another patient. Ask the lab for help.</Alert>;
  if (!doc) return <FormSkeleton />;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-sm">Your report</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <iframe title="Report" sandbox="" srcDoc={doc.html} className="h-[70vh] w-full rounded border bg-white" />
        <Button size="sm" onClick={() => printDocument(doc.html)}>
          Print
        </Button>
      </CardContent>
    </Card>
  );
}

export function addDaysIso(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function downloadPdf(base64: string, filename: string): void {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function PortalDocumentActions({ kind, id }: { kind: PortalDocumentKind; id: string }) {
  const [viewing, setViewing] = useState<RenderedDocument | null>(null);

  const view = useMutation({
    mutationFn: () => portalFetch<RenderedDocument>(`/portal/documents/${kind}/${id}?format=html`),
    onSuccess: setViewing,
  });
  const pdf = useMutation({
    mutationFn: () => portalFetch<RenderedDocument>(`/portal/documents/${kind}/${id}?format=pdf`),
    onSuccess: (document) => {
      if (document.pdfBase64) downloadPdf(document.pdfBase64, document.filename);
      else setViewing(document);
    },
  });
  const error = (view.error ?? pdf.error) as Error | null;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => view.mutate()} loading={view.isPending}>
          View
        </Button>
        <Button size="sm" variant="outline" onClick={() => pdf.mutate()} loading={pdf.isPending}>
          Download PDF
        </Button>
      </div>
      {error && <Alert variant="destructive">{error.message}</Alert>}
      {viewing && (
        <Dialog
          open
          onClose={() => setViewing(null)}
          title="Document"
          wide
          footer={
            <>
              <Button variant="outline" onClick={() => setViewing(null)}>
                Close
              </Button>
              <Button onClick={() => printDocument(viewing.html)}>Print</Button>
            </>
          }
        >
          <iframe title="Document" sandbox="" srcDoc={viewing.html} className="h-[70vh] w-full rounded border bg-white" />
        </Dialog>
      )}
    </div>
  );
}

/**
 * Self-service kiosk (SRS 11.2).
 *
 * Large type, one field, no sign-in: someone unwell is standing in front of it
 * holding their phone or their card.
 */
export function KioskPage() {
  const [search] = useSearchParams();
  const { t, locale } = useTranslation('portal', search.get('lang'));
  const [identifier, setIdentifier] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ tokenNo: string | null; doctorName: string; patient: { fullName: string } } | null>(
    null,
  );

  const checkIn = useMutation({
    mutationFn: (enteredDateOfBirth: string | void) =>
      portalFetch<{ tokenNo: string | null; doctorName: string; patient: { fullName: string } }>(
        '/portal/kiosk/check-in',
        { method: 'POST', body: { identifier, dateOfBirth: (enteredDateOfBirth ?? dateOfBirth) || undefined } },
      ),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      setIdentifier('');
      setDateOfBirth('');
      // Clear the screen for the next person rather than leaving a name on it.
      setTimeout(() => setResult(null), 20_000);
    },
    onError: (caught: Error) => {
      setResult(null);
      setError(caught.message);
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-6" lang={locale} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-xl text-center">
        {result ? (
          <div className="rounded-2xl border-2 border-success/40 bg-success/5 p-10">
            <p className="text-lg">{t('kiosk.checked_in', { name: result.patient.fullName })}</p>
            <p className="mt-6 text-sm uppercase tracking-widest text-muted-foreground">{t('kiosk.your_token')}</p>
            <p className="text-7xl font-bold tabular" dir="ltr">{result.tokenNo}</p>
            <p className="mt-4 text-muted-foreground">{t('kiosk.wait_for', { doctor: result.doctorName })}</p>
          </div>
        ) : (
          <>
            <h1 className="mb-2 text-3xl font-semibold">{t('kiosk.check_in')}</h1>
            <p className="mb-8 text-muted-foreground">{t('kiosk.hint')}</p>
            <Input
              dir="ltr"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="h-16 text-center text-2xl"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && identifier.length >= 3 && checkIn.mutate()}
            />
            <p className="mb-2 mt-5 text-sm text-muted-foreground">{t('kiosk.dob_hint')}</p>
            <DatePicker
              value={dateOfBirth}
              onChange={setDateOfBirth}
              onEnter={(entered) => identifier.length >= 3 && checkIn.mutate(entered)}
              className="h-12 [&_input]:text-center [&_input]:text-lg"
            />
            {error && (
              <Alert variant="destructive" className="mt-4">
                {error}
              </Alert>
            )}
            <Button
              className="mt-6 h-14 w-full text-lg"
              onClick={() => checkIn.mutate()}
              loading={checkIn.isPending}
              disabled={identifier.length < 3}
            >
              {t('kiosk.check_in')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
